<template>
    <LoaderComponent v-if="loading" />

    <div class="container mx-auto space-y-5">
        <!-- <ShipmentHeader /> -->

        <DangerAlert :message="errors.message" />

        <!-- header bar -->
        <div class="flex justify-between items-center py-2 mb-4">
            <h4 class="font-semibold font-inter text-xl sm:text-2xl">Inquiries</h4>
            <button @click="addInquiryFunc"
                class="inline-flex items-center justify-center rounded-2xl px-3 py-2 sm:px-4 sm:py-3 text-xs sm:text-sm font-semibold text-black bg-[#ffb61a] hover:bg-[#e9ac4b] transition duration-500">
                Add Ticket
            </button>
        </div>

        <!-- 1️⃣  each ticket = card (no table) -->
        <div class="w-full space-y-4 text-left bg-white">
            <template v-if="inquiries.data && inquiries.data.length">
                <div v-for="inquiry in inquiries.data" :key="inquiry.id"
                    class="border border-gray-500  rounded-lg shadow-sm hover:shadow-md transition">
                    <!-- header -->
                    <div class="flex items-center rounded-t-lg justify-between bg-[#F8EACA] px-4 py-3 border-b">
                        <!-- LEFT: Ticket ID + Date (stacked) -->
                        <div class="flex flex-col">
                            <span class="text-sm font-semibold text-black">
                                Ticket #{{ inquiry.id }}
                            </span>
                            <div class="flex text-sm justify-between">
                                <span class="font-inter font-semibold">Created:</span>
                                <span>{{ formatDate(inquiry.created_at) }}</span>
                            </div>
                        </div>

                        <!-- RIGHT: Status badge (kept on the same line) -->
                        <span class="text-xs sm:text-sm px-4 py-1 rounded-full font-semibold tracking-wide" :class="inquiry.status === 'open'
                            ? 'bg-white text-black border border-black'
                            : 'bg-red-200 text-red-700 border border-red-800'">
                            {{ inquiry.status === 'open' ? 'Open' : 'Closed' }}
                        </span>
                    </div>

                    <!-- body -->
                    <div class="p-4 space-y-2 text-sm text-black">
                        <div class="flex justify-between">
                            <span class="font-nexa font-semibold">Subject:</span>
                            <span class="font-medium">{{ inquiry.subject }}</span>
                        </div>
                        <div class="flex justify-between">
                            <span class="font-inter font-semibold">Department:</span>
                            <span>{{ inquiry.department }}</span>
                        </div>
                    </div>

                    <!-- footer actions -->
                    <div class="bg-gray-50 px-4 py-3 border-t rounded-b-xl flex justify-end">
                        <router-link :to="`/inquery/${inquiry.id}?message=1`"
                            class="inline-flex items-center gap-2 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-black bg-[#ffb61a] rounded-lg hover:bg-[#e9ac4b] transition">
                            <i class="fa-solid fa-message"></i>
                            View Messages
                        </router-link>
                    </div>
                </div>
            </template>

            <!-- empty state -->
            <div v-else class="flex flex-col items-center justify-center text-center p-6 mt-10">
                <h1 class="text-lg sm:text-xl font-semibold text-gray-700 mb-2">There are no tickets in the list.</h1>
                <img class="h-[65px] sm:h-[72px] w-[100px] sm:w-[120px] mt-2" src="../../assets/logo.svg"
                    alt="No Tickets">
            </div>
        </div>

        <!-- 2️⃣  pagination stays identical -->
        <Pagination @fetchRecords="fetchRecords" :pagination="inquiries" />

        <!-- 3️⃣  modal stays identical -->
        <div v-if="addInquiry"
            class="overflow-y-auto overflow-x-hidden fixed top-0 right-0 left-0 z-50 flex justify-center items-center w-full md:inset-0 max-h-full bg-[rgba(11,11,11,0.5)]">
            <div class="relative p-4 w-full max-w-2xl max-h-full">
                <div class="relative bg-white rounded-lg shadow">
                    <!-- header -->
                    <div class="flex items-center justify-between p-4 md:p-5 border-b rounded-t bg-[#F8EACA]">
                        <h3 class="text-xl font-semibold text-gray-900">New Ticket</h3>
                        <button @click="addInquiryFunc"
                            class="text-gray-400 bg-transparent hover:bg-[#f0bc42] hover:text-gray-900 rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center">
                            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 14 14">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" />
                            </svg>
                        </button>
                    </div>

                    <!-- body -->
                    <div class="p-4 md:p-5 space-y-4 max-h-[25rem] overflow-y-auto">
                        <!-- same form fields -->
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label class="block mb-2 text-sm font-medium text-gray-900">Name</label>
                                <input v-model="form.name" type="text" readonly required
                                    class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-[#FFD16E] focus:border-[#FFD16E] block w-full p-2.5">
                                <span class="text-red-600 text-base">{{ errors.name }}</span>
                            </div>
                            <div>
                                <label class="block mb-2 text-sm font-medium text-gray-900">Email</label>
                                <input v-model="form.email" type="email" readonly required
                                    class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-[#FFD16E] focus:border-[#FFD16E] block w-full p-2.5">
                                <span class="text-red-600 text-base">{{ errors.email }}</span>
                            </div>
                        </div>

                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label class="block mb-2 text-sm font-medium text-gray-900">Subject</label>
                                <input v-model="form.subject" type="text" required
                                    class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-[#FFD16E] focus:border-[#FFD16E] block w-full p-2.5">
                                <span class="text-red-600 text-base">{{ errors.subject }}</span>
                            </div>
                            <div>
                                <label class="block mb-2 text-sm font-medium text-gray-900">Category</label>
                                <select v-model="form.department" required
                                    class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-[#FFD16E] focus:border-[#FFD16E] block w-full p-2.5">
                                    <option disabled value="">Select a department</option>
                                    <option>General Inquiries</option>
                                    <option>Sales and Services</option>
                                    <option>Customer Support</option>
                                    <option>Billing and Payments</option>
                                    <option>Legal and Compliance</option>
                                    <option>Feedback and Suggestions</option>
                                </select>
                                <span class="text-red-600 text-base">{{ errors.department }}</span>
                            </div>
                        </div>

                        <div>
                            <label class="block mb-2 text-sm font-medium text-gray-900">Message</label>
                            <textarea v-model="form.message" rows="5" required
                                class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-[#FFD16E] focus:border-[#FFD16E] block w-full p-2.5"></textarea>
                            <span class="text-red-600 text-base">{{ errors.message }}</span>
                        </div>
                    </div>

                    <!-- footer -->
                    <div class="flex items-center justify-end gap-3 p-4 md:p-5 border-t border-gray-200 rounded-b">
                        <button @click="closeModal" type="button"
                            class="py-2.5 px-5 text-sm font-medium text-gray-900 bg-white rounded-lg border border-gray-200 hover:bg-gray-100 focus:ring-4 focus:ring-gray-100">Cancel</button>
                        <button @click="submit" type="button"
                            class="inline-flex items-center justify-center rounded-lg px-6 py-3 text-sm font-medium text-black bg-[#ffb61a] hover:bg-[#e9ac4b] transition duration-500">
                            Submit
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>


<script>
import { db } from '@/main';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import DangerAlert from '../Global/Alerts/DangerAlert.vue';
import LoaderComponent from "../Partials/LoaderComponent.vue";

export default {
    name: "AccountInquiries",
    data() {
        return {
            addInquiry: false,
            inquiries: [],
            loading: false,
            form: {
                name: "",
                email: "",
                subject: "",
                department: "",
                message: "",
            },
            errors: {}

        };
    },
    methods: {
        addInquiryFunc() {
            this.addInquiry = !this.addInquiry;
        },
        fetchRecords() {
            this.loading = true;
            const url = "inquiry/list";
            this.$axios
                .post(url, null, {
                    headers: this.$headers,
                })
                .then((response) => {
                    this.inquiries = response.data.data.inquiries;
                    this.form.name = response.data.data.user.name;
                    this.form.email = response.data.data.user.email;
                    this.loading = false;
                })
                .catch((error) => {
                    alert(error)
                    console.log(error);
                    this.loading = false;
                });
        },
        submit() {
            this.loading = true;
            this.errors = {};
            this.$axios
                .post("inquiry/create", this.form, {
                    headers: this.$headers
                })
                .then((response) => {
                    this.sendMessage(response.data.message);
                    console.log(response.data.message)
                })
                .catch((error) => {
                    this.errors = error.response.data.errors;
                })
                .finally(() => {
                    this.loading = false;
                    this.addInquiry = false;
                    this.fetchRecords();
                    this.form = {};
                });
        },
        closeModal() {
            this.addInquiry = false;
        },
        async sendMessage(message) {
            this.loading = true;
            this.errors = {};
            var checkValue = this.form.message.trim();
            if (checkValue.length > 0) {
                await addDoc(collection(db, 'inquiry_messages'), {
                    inquiry_id: message.inquiry_id,
                    user_id: message.user_id,
                    user_type: 'customer',
                    message: message.message,
                    created_at: serverTimestamp(),
                    updated_at: serverTimestamp()
                });
            }
        },
        formatDate(dateString) {
            const date = new Date(dateString);
            const day = String(date.getDate()).padStart(2, '0'); // Pad with leading zero
            const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-indexed
            const year = date.getFullYear();
            return `${day}-${month}-${year}`;
        },
    },
    mounted() {
        this.fetchRecords();
    },
    components: { DangerAlert, LoaderComponent },

};
</script>

<style>
#message-btn {
    background-color: Orange;
}

/* #message-btn:hover {
  background-color: RoyalBlue;
} */

/* .fa-solid, .fas {
  color: white;
} */
</style>